import React, { useState } from 'react';
import { useAuth, useLikes } from '../../contexts/AuthContext';
import { useAuthModal } from '../../contexts/AuthModalContext';
import { useCoinToast } from '../../contexts/CoinToastContext';
import { useErrorReport } from '../../contexts/ErrorReportContext';
import {
  isEventLiked,
  isLocationLiked,
  setEventLiked,
  setLocationLiked,
} from '../../services/userProfile';
import { promotePublicEventSaveTheDate } from '../../services/events/publicEventSaveTheDate';
import likeIcon from '../../assets/icons/like.svg';

/**
 * Like toggle for events or places. Guests open the Sign in / Sign up auth modal.
 * First like awards catalog coins.
 */
const LikeButton = ({
  event,
  place,
  className = '',
  showLabel = false,
  onEventLiked,
  onGuestActivate,
  earnLabel = '',
}) => {
  const { currentUser, userProfile, isSuperAdmin } = useAuth();
  const likes = useLikes();
  const { notifyWalkerEarn, notifyEarnStatus } = useCoinToast();
  const { openAuthModal } = useAuthModal();
  const { reportError } = useErrorReport();
  const [busy, setBusy] = useState(false);

  const isEvent = Boolean(event?.id);
  const id = isEvent ? event.id : place?.id;
  const liked = isEvent
    ? isEventLiked(likes, id)
    : isLocationLiked(likes, id);
  const displayLabel = liked ? 'Liked' : 'Like';
  const ariaLabelText = liked ? 'Unlike' : 'Like';

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!id) return;
    if (!currentUser) {
      if (typeof onGuestActivate === 'function') {
        onGuestActivate();
        return;
      }
      openAuthModal({ mode: 'signin' });
      return;
    }
    if (likes.disabled) return;
    setBusy(true);
    try {
      if (isEvent) {
        const result = await setEventLiked(currentUser.uid, event, !liked);
        if (result?.earned) {
          if (earnLabel) notifyEarnStatus(earnLabel);
          else notifyWalkerEarn(result.earned, userProfile);
        }
        if (onEventLiked) {
          await onEventLiked({ uid: currentUser.uid, event, liked: !liked });
        }
        if (isSuperAdmin && !liked) {
          try {
            await promotePublicEventSaveTheDate(event);
          } catch (promoteErr) {
            reportError(promoteErr, {
              pageLabel: 'Events',
              source: 'event-save-the-date',
              userMessage: 'Liked for you. Could not mark Save the date for everyone.',
            });
          }
        }
      } else {
        const result = await setLocationLiked(currentUser.uid, place, !liked);
        if (result?.earned) notifyWalkerEarn(result.earned, userProfile);
      }
      } catch (err) {
        console.error('Like toggle failed:', err);
        reportError(err, {
          pageLabel: isEvent ? 'Events' : 'Places',
          source: isEvent ? 'event-like' : 'place-like',
          userMessage: 'Could not update this save.',
        });
      } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      className={`like-btn ${liked ? 'like-btn--on' : ''} ${className}`.trim()}
      onClick={handleClick}
      disabled={busy}
      aria-pressed={liked}
      aria-label={ariaLabelText}
      title={ariaLabelText}
    >
      <img src={likeIcon} alt="" className="like-btn__icon" aria-hidden="true" draggable={false} />
      {showLabel && <span className="like-btn__label">{displayLabel}</span>}
    </button>
  );
};

export default LikeButton;
